import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

/**
 * What is about to be there, in its own shape.
 *
 * A spinner in the middle of a pane says only "wait". These say what is coming and
 * where it will sit, so the pane does not jump when it lands: the transcript keeps
 * its alternating names and its right-hand timestamps, the list keeps its rows.
 *
 * Widths alternate on the index rather than at random. Messages are not uniform, and
 * a column of identical bars reads as a loading graphic instead of as text.
 */

/**
 * A line of the real thing, at the real thing's height.
 *
 * The bar is shorter than the line it sits in, as text is, and the line owns the
 * height. Sizing the bars themselves left every skeleton a few pixels short of what
 * replaced it, which is a jump on every load.
 */
const Line = ({
  height,
  className,
  children,
}: {
  height: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <Stack
    direction="row"
    align="center"
    gap={0}
    className={cn(height, className)}
  >
    <Stack className="w-full" direction="row" align="center" gap={2}>
      {children}
    </Stack>
  </Stack>
);

/**
 * One message: author and timestamp on a line, then a line or two of body.
 *
 * Spaced the way the transcript spaces messages — pt-4 between them, nothing above
 * the first — and NOT divided by hairlines. The skeleton drew rules the real
 * transcript has never had.
 */
const MessageSkeleton = ({ index }: { index: number }) => (
  <Stack
    gap={0}
    align="stretch"
    className={cn("w-full", { "pt-0": index === 0, "pt-4": index !== 0 })}
  >
    {/* 20px of name-and-timestamp over 21px lines of body: a one-line message
        measures 41px, which is what this measures. */}
    <Line height="h-5">
      <Skeleton className="h-3.5 w-28 rounded-full" />
      <Skeleton className="ml-auto h-3 w-20 rounded-full" />
    </Line>
    <Line height="h-[21px]">
      <Skeleton
        className={cn({
          "h-3.5 w-4/5": index % 2 === 0,
          "h-3.5 w-full": index % 2 !== 0,
        })}
      />
    </Line>
    {/* The first message stays one line, which is the 41px a real one measures.
        Later ones vary, because messages do. */}
    {index % 2 === 1 && (
      <Line height="h-[21px]">
        <Skeleton className="h-3.5 w-1/2" />
      </Line>
    )}
  </Stack>
);

/** The transcript, mid-conversation. */
export const PackChatTranscriptSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <Stack className="w-full" gap={0} align="stretch" aria-hidden>
    {Array.from({ length: rows }, (_, index) => (
      <MessageSkeleton key={index} index={index} />
    ))}
  </Stack>
);

/**
 * One row of the technician list.
 *
 * 10px of padding over three lines of 16, 18 and 22px, which is exactly the row it
 * stands in for: name, then the count and recency, then the last message.
 */
const RowSkeleton = ({ index }: { index: number }) => (
  <Stack gap={0} align="stretch" className="w-full px-3 py-2.5">
    <Line height="h-4">
      <Skeleton className="h-3 w-24 rounded-full" />
      {/* The state dot's reserved gutter, so rows do not shift when it appears. */}
      <Skeleton className="ml-auto size-[7px] shrink-0 rounded-full" />
    </Line>
    <Line height="h-[18px]">
      <Skeleton className="h-2.5 w-32 rounded-full" />
    </Line>
    <Line height="h-[22px]">
      <Skeleton
        className={cn({
          "h-3 w-full": index % 3 === 0,
          "h-3 w-3/4": index % 3 !== 0,
        })}
      />
    </Line>
  </Stack>
);

/**
 * The list column's own bar and filters, at their real heights.
 *
 * 53px of search over a 40px filter row: without them the rows started 93px up the
 * column and every one of them dropped when the data landed.
 */
const ListChromeSkeleton = () => (
  <>
    <Stack gap={0} align="stretch" className="border-b border-line px-5 py-2">
      <Line height="h-9">
        <Skeleton className="size-4 shrink-0 rounded-full" />
        <Skeleton className="h-3 w-28 rounded-full" />
      </Line>
    </Stack>
    <Stack gap={0} align="stretch" className="px-5 pt-3 pb-2">
      <Line height="h-5">
        <Skeleton className="h-3 w-10 rounded-full" />
        <Skeleton className="h-3 w-14 rounded-full" />
        <Skeleton className="h-3 w-14 rounded-full" />
      </Line>
    </Stack>
  </>
);

/** The queue, before it arrives. Enough rows to reach the bottom of the column. */
export const PackChatListSkeleton = ({ rows = 10 }: { rows?: number }) => (
  <Stack className="w-full" gap={0} align="stretch" aria-hidden>
    <ListChromeSkeleton />
    <Stack gap={0} align="stretch" className="w-full divide-y divide-line">
      {Array.from({ length: rows }, (_, index) => (
        <RowSkeleton key={index} index={index} />
      ))}
    </Stack>
  </Stack>
);

/**
 * The reading pane before anything is selected: a header that will name somebody, a
 * few messages, and the box to answer in.
 *
 * Includes the header and the composer because they are what makes the pane read as a
 * conversation. Without them the pane was bars floating in space, and the layout
 * jumped when the real thing arrived.
 */
export const PackChatConversationSkeleton = () => (
  <Stack gap={0} align="stretch" aria-hidden className="h-full min-h-0 w-full">
    {/* 16px of padding over a 24px name and a 16px meta line: the 73px the real
        header measures, so the transcript starts where it will stay. */}
    <Stack
      direction="row"
      align="start"
      justify="between"
      gap={4}
      className="w-full shrink-0 border-b border-line px-5 py-4"
    >
      <Stack gap={0} align="stretch" className="min-w-0">
        <Line height="h-6">
          <Skeleton className="h-4 w-40 rounded-full" />
        </Line>
        <Line height="h-4">
          <Skeleton className="h-3 w-56 rounded-full" />
        </Line>
      </Stack>
      <Line height="h-6">
        <Skeleton className="h-3.5 w-24 rounded-full" />
      </Line>
    </Stack>

    <Stack gap={0} align="stretch" className="min-h-0 flex-1 px-5 py-4">
      <Stack gap={0} align="stretch" className="mx-auto w-full max-w-5xl">
        <PackChatTranscriptSkeleton rows={4} />
      </Stack>
    </Stack>

    {/* The reply box is 50px tall inside 12px of padding. */}
    <Stack
      gap={0}
      align="stretch"
      className="shrink-0 border-t border-line px-5 py-3"
    >
      <Stack gap={0} align="stretch" className="mx-auto w-full max-w-5xl">
        <Skeleton className="h-[50px] w-full rounded-lg" />
      </Stack>
    </Stack>
  </Stack>
);

/**
 * The pack column, which is there whenever a conversation is.
 *
 * Rendered while the list loads so the column is not a 330px blank beside a pane
 * that has already drawn itself. The shape follows the real pack: the period and its
 * total, then the label-over-value pairs.
 */
export const PackChatPackSkeleton = () => (
  <Stack gap={6} align="stretch" aria-hidden className="w-full px-4 py-4">
    <Stack gap={1} align="stretch">
      <Skeleton className="h-3 w-48 rounded-full" />
      <Skeleton className="h-8 w-36" />
    </Stack>
    <Stack gap={0} align="stretch" className="grid grid-cols-2 gap-x-4 gap-y-4">
      {Array.from({ length: 6 }, (_, index) => (
        <Stack key={index} gap={1} align="stretch" className="min-w-0">
          <Skeleton className="h-3 w-20 rounded-full" />
          <Skeleton
            className={cn({
              "h-4 w-24": index % 2 === 0,
              "h-4 w-16": index % 2 !== 0,
            })}
          />
        </Stack>
      ))}
    </Stack>
  </Stack>
);
